// Mobile UI route table — the Flutter navigation graph.
//
// Flutter pushes MaterialPageRoutes; here each of those destinations is a route
// so the Android hardware back button and deep links behave the same way.
//
//   /splash                  splashScreen.dart      (boot gate)
//   /login /signup /forgot   the three auth screens
//   /blocked                 blockedScreen.dart
//   /home?tab=N              homeScreen.dart        (4 bottom tabs)
//   /rooms/:locationId       roomsScreen.dart
//   /room/:roomId/devices    devicesScreen.dart
//   /device/:uniqueNumber    screenRedirector()     -> per-type control screen
//
// Screens that are identical on both form factors (Wall Mode, per-room Scenes,
// Profile, About, Schedules) are reused from ui/desktop rather than duplicated —
// they read the same core logic, which is the whole point of the split.

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useApp } from '../../core/state/AppContext';
import { kioskLandingRoute } from '../../core/wall/kioskMode';
import { MessageDialogProvider } from './components/MessageDialog';

import SplashScreen from './screens/SplashScreen';
import BlockedScreen from './screens/BlockedScreen';
import LoginScreen from './screens/LoginScreen';
import SignUpScreen from './screens/SignUpScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import HomeScreen from './screens/HomeScreen';
import RoomsScreen from './screens/RoomsScreen';
import DevicesScreen from './screens/DevicesScreen';
import DeviceScreen from './screens/device/DeviceScreen';
import LocationDataScreen from './screens/LocationDataScreen';
import WifiOnboardScreen from './screens/WifiOnboardScreen';
import AddDeviceScreen from './screens/AddDeviceScreen';
import BleTestScreen from './screens/BleTestScreen';
import MqttConsoleScreen from './screens/MqttConsoleScreen';
import TuyaTestingScreen from './screens/TuyaTestingScreen';
import FirmwareScreen from './screens/FirmwareScreen';

// Shared with the desktop shell — same component, same core logic.
import RoomScenesScreen from '../desktop/pages/scenes/RoomScenesScreen';
import ProfileScreen from '../desktop/pages/ProfileScreen';
import AboutScreen from '../desktop/pages/AboutScreen';
import SchedulesScreen from '../desktop/pages/SchedulesScreen';
import MobileShell from './components/MobileShell';
import WallModeScreen from '../desktop/pages/wall/WallModeScreen';
import WallPanelSetupScreen from '../shared/WallPanelSetupScreen';

// ── The admin screens, reused rather than rewritten ────────────────────────
// Same rule as Profile/About/Schedules above: one component, both shells. These
// six existed only in DesktopRoutes, so a Super Admin holding a phone could not
// reach them at all — which is the gap this closes.
//
// ⚠️ FOUR OF THEM ARE BUILT FOR A WIDE SCREEN and they are routed here anyway,
// deliberately: reachable-and-cramped beats unreachable, and they carry their
// own AppBar with a back button so they are self-contained inside MobileShell.
// UserAccess (a four-panel transfer list) and DeviceAdmin (a ~60-field form
// distilled to a wizard) are the two that genuinely want a phone layout of
// their own; until then they scroll. Do not "fix" that by hiding the routes.
import CompaniesScreen from '../desktop/pages/CompaniesScreen';
import DeviceTypesScreen from '../desktop/pages/DeviceTypesScreen';
import DeviceAdminScreen from '../desktop/pages/DeviceAdminScreen';
import UserAccessScreen from '../desktop/pages/UserAccessScreen';
import InstalledDevicesScreen from '../desktop/pages/InstalledDevicesScreen';
import KioskSetupScreen from '../desktop/pages/KioskSetupScreen';

function Protected({ children, bare = false }) {
  const { isAuthed, signingIn } = useApp();
  // Same rule as desktop: a saved sign-in in flight is "not yet decided", not
  // signed-out. Without this the login screen flashes before the session lands.
  if (signingIn) return <SplashScreen />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  // ── The identity strip and company footer live HERE ──────────────────────
  // Attaching them to `Protected` rather than to each screen means every
  // authenticated route has them by construction, and a screen added tomorrow
  // cannot forget. The pre-auth routes below are deliberately not wrapped:
  // there is no user yet to name.
  //
  // `bare` opts a route out — Wall Mode is a kiosk that deliberately fills the
  // panel edge to edge, and chrome on it would be wrong rather than missing.
  return bare ? children : <MobileShell>{children}</MobileShell>;
}

// ── Permission, at the ROUTE, the way the desktop shell has always done it ──
//
// ⚠️ HIDING A TILE IS NOT A GATE. These four routes carried comments reading
// "Super Admin only, gated in MeTab" — and that was the whole of it: MeTab
// simply did not draw the tile. The route stayed live, so any signed-in
// customer reaching /mqtt, /firmware, /tuya-testing or /ble-test by typed URL,
// deep link, restored tab or Back got the screen. Three of the four then
// refused on their own; /ble-test had no check of any kind and rendered.
//
// These are not read-only pages. /firmware reflashes hardware estate-wide,
// /tuya-testing rewrites a device's type and reboots it, /mqtt publishes
// arbitrary commands to any topic.
//
// DesktopRoutes gates each of these at the route AND each screen re-checks
// inside. Mobile now does both too — one layer is one edit away from being
// none.
function AdminOnly({ children, allow }) {
  return allow ? children : <Navigate to="/home" replace />;
}

export default function MobileRoutes() {
  const { isAuthed, isSuperAdmin, canTuyaTest } = useApp();

  return (
    <MessageDialogProvider>
      <Routes>
        <Route path="/splash" element={<SplashScreen />} />
        <Route path="/blocked" element={<BlockedScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/signup" element={<SignUpScreen />} />
        <Route path="/forgot" element={<ForgotPasswordScreen />} />

        <Route path="/home" element={<Protected><HomeScreen /></Protected>} />
        <Route path="/rooms/:locationId" element={<Protected><RoomsScreen /></Protected>} />
        <Route path="/room/:roomId/devices" element={<Protected><DevicesScreen /></Protected>} />
        <Route path="/device/:uniqueNumber" element={<Protected><DeviceScreen /></Protected>} />
        <Route path="/location/:locationId" element={<Protected><LocationDataScreen /></Protected>} />
        {/* BLE Wi-Fi onboarding — hand a brand-new device its home Wi-Fi. */}
        <Route path="/wifi-setup" element={<Protected><WifiOnboardScreen /></Protected>} />
        {/* Claim an existing device: by its own number, or by a share code.
            Both render AddDeviceScreen, which adapts its wording. */}
        <Route path="/add-device" element={<Protected><AddDeviceScreen /></Protected>} />
        <Route path="/scan-share" element={<Protected><AddDeviceScreen /></Protected>} />
        {/* Firmware bring-up tool — Super Admin. Gated here, in MeTab, and
            inside the screen. */}
        <Route
          path="/ble-test"
          element={<Protected><AdminOnly allow={isSuperAdmin}><BleTestScreen /></AdminOnly></Protected>}
        />
        {/* Smartify Message Box — Super Admin. Publishes arbitrary commands. */}
        <Route
          path="/mqtt"
          element={<Protected><AdminOnly allow={isSuperAdmin}><MqttConsoleScreen /></AdminOnly></Protected>}
        />
        {/* Production bench — AllowTestingTuya (or Super Admin), NOT
            IsSuperAdmin alone; the bench operators granted this flag in the
            backend are not Super Admins. See AppContext's canTuyaTest. */}
        <Route
          path="/tuya-testing"
          element={<Protected><AdminOnly allow={canTuyaTest}><TuyaTestingScreen /></AdminOnly></Protected>}
        />
        {/* Estate-wide firmware list — Super Admin. Reflashes hardware. */}
        <Route
          path="/firmware"
          element={<Protected><AdminOnly allow={isSuperAdmin}><FirmwareScreen /></AdminOnly></Protected>}
        />

        {/* ── Admin screens, all Super Admin — they write ownership, device
            types, company info and other people's access. Gated here AND
            inside each screen, same as the four above. */}
        <Route
          path="/companies"
          element={<Protected><AdminOnly allow={isSuperAdmin}><CompaniesScreen /></AdminOnly></Protected>}
        />
        <Route
          path="/device-types"
          element={<Protected><AdminOnly allow={isSuperAdmin}><DeviceTypesScreen /></AdminOnly></Protected>}
        />
        <Route
          path="/device-admin"
          element={<Protected><AdminOnly allow={isSuperAdmin}><DeviceAdminScreen /></AdminOnly></Protected>}
        />
        <Route
          path="/user-access"
          element={<Protected><AdminOnly allow={isSuperAdmin}><UserAccessScreen /></AdminOnly></Protected>}
        />
        <Route
          path="/installed"
          element={<Protected><AdminOnly allow={isSuperAdmin}><InstalledDevicesScreen /></AdminOnly></Protected>}
        />

        {/* NOT admin-gated, and the one that most needed to be here: a wall
            tablet runs THIS shell, so the screen that turns kiosk mode on was
            unreachable from the very device it configures. */}
        <Route path="/wall/kiosk" element={<Protected><KioskSetupScreen /></Protected>} />

        <Route path="/room/:roomId/scenes" element={<Protected><RoomScenesScreen /></Protected>} />
        <Route path="/schedule/:uniqueNumber/:output" element={<Protected><SchedulesScreen /></Protected>} />
        <Route path="/profile" element={<Protected><ProfileScreen /></Protected>} />
        <Route path="/about" element={<Protected><AboutScreen /></Protected>} />
        {/* Kiosk: fills the panel, no app chrome. */}
        {/* One wall editor — /wall/setup. See the note in DesktopRoutes. */}
        <Route path="/wall" element={<Protected bare><WallModeScreen /></Protected>} />
        <Route path="/wall/setup" element={<Protected><WallPanelSetupScreen /></Protected>} />

        {/* A wall tablet lands on its panel instead of the normal home. Only the
            LANDING route is redirected — forcing /wall on every navigation would
            make the designer unreachable from the very tablet it configures. */}
        <Route
          path="*"
          element={
            // eslint-disable-next-line no-nested-ternary
            !isAuthed ? <Navigate to="/login" replace />
              : <Navigate to={kioskLandingRoute() || '/splash'} replace />
          }
        />
      </Routes>
    </MessageDialogProvider>
  );
}
